import React, { ChangeEvent, ReactElement, useEffect, useRef, useState } from "react";
import { Dropdown } from "@zendeskgarden/react-dropdowns";
import {Field as FormField, Input, Radio} from "@zendeskgarden/react-forms";
import { TooltipModal } from "@zendeskgarden/react-modals";

import downChevron from "../../../../../../assets/icons/chevron/down.svg";
import { MonthlyAdjustmentType, MonthlyAverageBasePeriod } from "../../../../../../__generated__/generated_types";
import { IFormulaBarUpdates } from "../../../../../../contexts/account/data/useFormulaBarUpdates";
import MonthPicker, { MonthPickerType } from "../month-picker/MonthPicker";
import { useProperties } from "../../../../../../contexts/properties/PropertiesContext";
import { MONTHS } from "../../../../../../constants/Months";
import { stringToFixedFloat } from "../../logic/utils";

import * as css from "./styles/css.module.scss";
import * as monthlyAvgCss from "./styles/monthly-average-menu/css.module.scss";


enum BasePeriodInputType {
    MONTH_RANGE = "MONTH_RANGE",
    LOOKBACK = "LOOKBACK",
}

interface IGrowthMenuProps {
    fbUpdates: IFormulaBarUpdates,
    handleCloseParent?: () => void,
    level: number,
    triggerNode?: ReactElement,
}

export default function YearlyAverageMenu({
    fbUpdates,
    // handleCloseParent,
    level,
    triggerNode,
}: IGrowthMenuProps): React.ReactElement {
    const { currentProperty } = useProperties();

    const [startMonth, setStartMonth] = useState<number>();
    const [endMonth, setEndMonth] = useState<number>();
    const [startYear, setStartYear] = useState<number>();
    const [endYear, setEndYear] = useState<number>();
    const [minMonth, setMinMonth] = useState<number>(0);
    const [maxMonth, setMaxMonth] = useState<number>(11);
    const [minYear, setMinYear] = useState<number>(0);
    const [maxYear, setMaxYear] = useState<number>(2000);
    const [refElement, setRefElement] = useState<HTMLDivElement|null>(null);
    const [startPeriodRefElement, setStartPeriodRefElement] = useState<HTMLDivElement|null>(null);
    const [endPeriodRefElement, setEndPeriodRefElement] = useState<HTMLDivElement|null>(null);

    const [selectedBasePeriodInputType, setSelectedBasePeriodInputType] = useState<BasePeriodInputType>(BasePeriodInputType.MONTH_RANGE);

    const monthlyAverageBasePeriodFromFxBar = fbUpdates.parsedFormDrivers.yearlyAverage[0]?.monthlyAverageBasePeriod;
    useEffect(() => {
        if (monthlyAverageBasePeriodFromFxBar) {
            setSelectedBasePeriodInputType(monthlyAverageBasePeriodFromFxBar === MonthlyAverageBasePeriod.CustomLookbackPeriod ? BasePeriodInputType.MONTH_RANGE : BasePeriodInputType.LOOKBACK);
        }
    }, [monthlyAverageBasePeriodFromFxBar]);

    const monthlyAdjustmentType = fbUpdates.parsedFormDrivers.yearlyAverage[0]?.monthlyAdjustmentType;
    const monthlyAdjustmentValue = fbUpdates.parsedFormDrivers.yearlyAverage[0]?.monthlyAdjustmentValue;
    const [dollarMonthlyAdjustment, setDollarMonthlyAdjustment] = useState<string>(monthlyAdjustmentType === MonthlyAdjustmentType.Dollar ? (typeof monthlyAdjustmentValue === "number" ? monthlyAdjustmentValue.toString() : "0") : "0");
    const [percentMonthlyAdjustment, setPercentMonthlyAdjustment] = useState<string>(monthlyAdjustmentType === MonthlyAdjustmentType.Percent ? (typeof monthlyAdjustmentValue === "number" ? monthlyAdjustmentValue.toString() : "0") : "0");

    const triggerRef = useRef<HTMLDivElement>(null);
    const startPeriodRef = useRef<HTMLDivElement>(null);
    const endPeriodRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (currentProperty) {
            // for acquisition properties, getting the t12 date range requires looking at the start month
            if (currentProperty.isAcquisitionProperty && currentProperty.acquisitionBudgetYear && currentProperty.acquisitionBudgetStartMonth) {
                const defaultStartMonth = currentProperty.acquisitionBudgetStartMonth;
                const defaultEndMonth = defaultStartMonth == 0 ? 11 : defaultStartMonth - 1;
                const defaultEndYear = defaultStartMonth == 0 ? currentProperty.acquisitionBudgetYear - 1 : currentProperty.acquisitionBudgetYear;
                const defaultStartYear = defaultStartMonth == 0 ? defaultEndYear : defaultEndYear - 1;

                setStartMonth(defaultStartMonth);
                setEndMonth(defaultEndMonth);
                setStartYear(defaultStartYear);
                setEndYear(defaultEndYear);
            } else {
                // Default end month should be the last month of actuals
                setEndMonth(currentProperty.reforecastStartMonthIndex - 1);
                setStartYear(currentProperty.reforecastYear);
                setEndYear(currentProperty.reforecastYear);
            }

            // setup default selected months/years
            let initialMinMonth = 0;
            let initialMaxMonth = 11;
            let initialMinYear = currentProperty.reforecastYear - 2;
            let initialMaxYear = currentProperty.reforecastYear - 1;

            if (currentProperty.reforecastStartMonthIndex != 0) {
                initialMaxYear = currentProperty.reforecastYear;
                initialMaxMonth = currentProperty.reforecastStartMonthIndex - 1;
            }

            if (currentProperty.isAcquisitionProperty && currentProperty.acquisitionBudgetStartMonth != null && currentProperty.acquisitionBudgetYear) {
                initialMinMonth = currentProperty.acquisitionBudgetStartMonth;
                initialMaxMonth = initialMinMonth == 0 ? 11 : initialMinMonth - 1;
                initialMaxYear = initialMinMonth == 0 ? currentProperty.acquisitionBudgetYear - 1 : currentProperty.acquisitionBudgetYear;
                initialMinYear = initialMinMonth == 0 ? initialMaxYear : initialMaxYear - 1;
            }

            setMinMonth(initialMinMonth);
            setMaxMonth(initialMaxMonth);
            setMinYear(initialMinYear);
            setMaxYear(initialMaxYear);
        }
    }, [currentProperty]);

    /**
     * Default the month-picker selections to what's coming from the backend
    */
    useEffect(() => {
        const lookbackPeriodStartMonth = fbUpdates.parsedFormDrivers.yearlyAverage[0]?.lookbackPeriodStart?.split("-")[1];
        const lookbackPeriodStartYear = fbUpdates.parsedFormDrivers.yearlyAverage[0]?.lookbackPeriodStart?.split("-")[0];
        const lookbackPeriodEndMonth = fbUpdates.parsedFormDrivers.yearlyAverage[0]?.lookbackPeriodEnd?.split("-")[1];
        const lookbackPeriodEndYear = fbUpdates.parsedFormDrivers.yearlyAverage[0]?.lookbackPeriodEnd?.split("-")[0];

        if (lookbackPeriodStartMonth && lookbackPeriodEndMonth && lookbackPeriodStartYear && lookbackPeriodEndYear) {
            setStartMonth(parseInt(lookbackPeriodStartMonth) - 1);
            setStartYear(parseInt(lookbackPeriodStartYear));
            setEndMonth(parseInt(lookbackPeriodEndMonth) - 1);
            setEndYear(parseInt(lookbackPeriodEndYear));
        }
    }, [fbUpdates.parsedFormDrivers.yearlyAverage[0]?.id]);

    useEffect(() => {
        if (selectedBasePeriodInputType === BasePeriodInputType.LOOKBACK) {
            setStartPeriodRefElement(null);
            setEndPeriodRefElement(null);
        }
    }, [selectedBasePeriodInputType]);

    // following two useEffects are to keep the dates within 11 months of each other, so when one changes
    // if the other is outside of that range, it clamps it
    useEffect(() => {
        if (startMonth != null && startYear != null && endMonth != null && endYear != null) {
            const startDateTotalMonths = startYear * 12 + startMonth;
            const endDateTotalMonths = endYear * 12 + endMonth;
            const monthGap = endDateTotalMonths - startDateTotalMonths;
            fbUpdates.updateYearlyAverageStartMonthYear(startMonth, startYear);
            setStartPeriodRefElement(null);

            if (monthGap > 11) {
                const newEndMonth = startDateTotalMonths + 11;
                setEndMonth(newEndMonth % 12);
                setEndYear(Math.floor(newEndMonth / 12));
            }
        }
    }, [startMonth, startYear]);

    useEffect(() => {
        if (startMonth != null && startYear != null && endMonth != null && endYear != null) {
            const startDateTotalMonths = startYear * 12 + startMonth;
            const endDateTotalMonths = endYear * 12 + endMonth;
            const monthGap = endDateTotalMonths - startDateTotalMonths;
            fbUpdates.updateYearlyAverageEndMonthYear(endMonth, endYear);
            setStartPeriodRefElement(null);

            if (monthGap > 11) {
                const newStartMonth = endDateTotalMonths - 11;
                setStartMonth(newStartMonth % 12);
                setStartYear(Math.floor(newStartMonth / 12));
            }
        }
    }, [endMonth, endYear]);

    const beforeCloseNode = (value: string, validate = false) => {
        if (!currentProperty) {
            return;
        }

        if (value === '-' || value.length === 0) {
            fbUpdates.updateYearlyAdjustmentValue(0, currentProperty);
        } else {
            if (validate && (parseFloat(value) < -100 || parseFloat(value) > 100)) {
                fbUpdates.updateYearlyAdjustmentValue(0, currentProperty);
                setPercentMonthlyAdjustment("0");
            }  else {
                fbUpdates.updateYearlyAdjustmentValue(stringToFixedFloat(value, 2, false), currentProperty);
            }
        }
    };

    useEffect(() => {
        if (monthlyAdjustmentType === MonthlyAdjustmentType.Dollar) {
            beforeCloseNode(dollarMonthlyAdjustment);
        } else if (monthlyAdjustmentType === MonthlyAdjustmentType.Percent) {
            beforeCloseNode(percentMonthlyAdjustment, true);
        }
    }, [monthlyAdjustmentType]);

    const handleRootClick = () => {
        setRefElement(triggerRef.current);
    };

    const handleStartPeriodClick = () => {
        setEndPeriodRefElement(null);
        // To remove the overlap period when both popovers are visible
        setTimeout(() => setStartPeriodRefElement(startPeriodRef.current), 200);
    };

    const handleEndPeriodClick = () => {
        setStartPeriodRefElement(null);
        // To remove the overlap period when both popovers are visible
        setTimeout(() => setEndPeriodRefElement(endPeriodRef.current), 200);
    };

    const handleCloseRoot = () => {
        setRefElement(null);
    };

    const handleCloseStartPeriodPopover = () => {
        setStartPeriodRefElement(null);
    };

    const handleCloseEndPeriodPopover = () => {
        setEndPeriodRefElement(null);
    };

    const handleCloseMonthlyAdjustmentNode = () => {
        if (monthlyAdjustmentType === MonthlyAdjustmentType.Dollar) {
            beforeCloseNode(dollarMonthlyAdjustment);
        } else if (monthlyAdjustmentType === MonthlyAdjustmentType.Percent) {
            beforeCloseNode(percentMonthlyAdjustment, true);
        }

        handleCloseRoot();
    };

    const handleSetSelectedLookbackOptionAsMonthRange = () => {
        setSelectedBasePeriodInputType(BasePeriodInputType.MONTH_RANGE);
        fbUpdates.updateYearlyAverageBasePeriod(MonthlyAverageBasePeriod.CustomLookbackPeriod);
    };

    const handleDollarMonthlyAdjustmentChange = (event: ChangeEvent<HTMLInputElement>) => {
        setDollarMonthlyAdjustment(event.target.value);
    };

    const handlePercentMonthlyAdjustmentChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPercentMonthlyAdjustment(event.target.value);
    };

    /**
     * Close the yearly adjustment popup if the user presses enter while in the input field
     */
    const handleMonthlyAdjustmentInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleCloseMonthlyAdjustmentNode();
        }
    };

    if (!currentProperty || !startYear || !endYear) {
        return <></>;
    }

    const chevronDownIcon = <img className={monthlyAvgCss.chevDown} src={downChevron} alt="down" width="12px" height="12px" />;

    const rootDropdown = (
        <Dropdown
            isOpen
            onSelect={(_item) => null}
            downshiftProps={{ defaultHighlightedIndex: 0 }}
        >
            <div className={css.menuWrapper} style={{marginBottom: "1rem"}}>
                <div className={monthlyAvgCss.menuHeader}>
                    Select range to average
                </div>
                {
                    startMonth != null && endMonth != null &&

                        <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.radioRow} ${monthlyAvgCss.basePeriodRadioRow}`}>
                            <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.radioCol}`}>
                                <FormField>
                                    <Radio
                                        key={BasePeriodInputType.MONTH_RANGE}
                                        name={BasePeriodInputType.MONTH_RANGE}
                                        value={BasePeriodInputType.MONTH_RANGE}
                                        checked={selectedBasePeriodInputType === BasePeriodInputType.MONTH_RANGE}
                                        onChange={handleSetSelectedLookbackOptionAsMonthRange}
                                    />
                                </FormField>
                            </div>
                            <div className={monthlyAvgCss.dateRangeLabel}>
                                Average actuals from&nbsp;
                            </div>
                            <div className={monthlyAvgCss.monthPickerTarget}>
                                <div
                                    className={css.triggerNode}
                                    ref={startPeriodRef}
                                    onClick={handleStartPeriodClick}
                                    style={inlineStyles.triggerNode}
                                >
                                    {`${MONTHS[startMonth]} ${currentProperty.isAcquisitionProperty ? 'T12' : startYear}`}
                                    {chevronDownIcon}
                                </div>
                                <div className={monthlyAvgCss.monthPickerTargetChild}>
                                    <TooltipModal
                                        className={css.tooltipModal}
                                        hasArrow={false}
                                        referenceElement={startPeriodRefElement}
                                        onClose={handleCloseStartPeriodPopover}
                                        placement="bottom"
                                        isAnimated
                                    >
                                        <TooltipModal.Body className={css.tooltipModalBody}>
                                            <div className={`${css.menuWrapper} ${monthlyAvgCss.menuWrapper}`}>
                                                <Dropdown
                                                    isOpen
                                                    onSelect={(_item) => null}
                                                    downshiftProps={{ defaultHighlightedIndex: 0 }}
                                                >
                                                    <div style={inlineStyles.rootMenu}>
                                                        <MonthPicker
                                                            minMonth={minMonth}
                                                            maxMonth={endMonth - 1}
                                                            minYear={minYear}
                                                            maxYear={endYear}
                                                            defaultMonth={startMonth}
                                                            defaultYear={startYear}
                                                            setMonth={setStartMonth}
                                                            setYear={setStartYear}
                                                            type={MonthPickerType.FROM}
                                                            property={currentProperty}
                                                        />
                                                    </div>
                                                </Dropdown>
                                            </div>
                                        </TooltipModal.Body>
                                    </TooltipModal>
                                </div>
                            </div>
                            <div className={monthlyAvgCss.dateRangeLabel}>
                                &nbsp;to&nbsp;
                            </div>
                            <div className={monthlyAvgCss.monthPickerTarget}>
                                <div
                                    className={css.triggerNode}
                                    ref={endPeriodRef}
                                    onClick={handleEndPeriodClick}
                                    style={inlineStyles.triggerNode}
                                >
                                        {`${MONTHS[endMonth]} ${currentProperty.isAcquisitionProperty ? 'T12' : endYear}`}
                                        {chevronDownIcon}
                                </div>
                                <div className={monthlyAvgCss.monthPickerTargetChild}>
                                    <TooltipModal
                                        className={css.tooltipModal}
                                        hasArrow={false}
                                        referenceElement={endPeriodRefElement}
                                        onClose={handleCloseEndPeriodPopover}
                                        placement="bottom"
                                        isAnimated
                                    >
                                        <TooltipModal.Body className={css.tooltipModalBody}>
                                            <div className={`${css.menuWrapper} ${monthlyAvgCss.menuWrapper}`}>
                                                <Dropdown
                                                    isOpen
                                                    onSelect={(_item) => null}
                                                    downshiftProps={{ defaultHighlightedIndex: 0 }}
                                                >
                                                    <div style={inlineStyles.rootMenu}>
                                                        <MonthPicker
                                                            minMonth={startMonth + 1}
                                                            maxMonth={maxMonth}
                                                            minYear={startYear}
                                                            maxYear={maxYear}
                                                            defaultMonth={endMonth}
                                                            defaultYear={endYear}
                                                            setMonth={setEndMonth}
                                                            setYear={setEndYear}
                                                            type={MonthPickerType.TO}
                                                            property={currentProperty}
                                                        />
                                                    </div>
                                                </Dropdown>
                                            </div>
                                        </TooltipModal.Body>
                                    </TooltipModal>
                                </div>
                            </div>
                        </div>
                }
            </div>
        </Dropdown>
    );

    const monthlyAdjustmentForm = (
        <div className={`${css.menuWrapper} ${monthlyAvgCss.adjustmentWrapper}`}>
            <div className={monthlyAvgCss.col}>
                <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.menuHeader}`}>
                    Input yearly adjustment
                </div>
                <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.radioRow}`}>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.radioCol}`}>
                        <FormField>
                            <Radio
                                name={MonthlyAdjustmentType.Percent}
                                value={MonthlyAdjustmentType.Percent}
                                checked={fbUpdates.parsedFormDrivers.yearlyAverage[0]?.monthlyAdjustmentType === MonthlyAdjustmentType.Percent}
                                onChange={() => fbUpdates.updateYearlyAdjustmentType(MonthlyAdjustmentType.Percent)}
                            />
                        </FormField>
                    </div>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.inputCol} ${percentMonthlyAdjustment !== undefined && monthlyAvgCss.percentInput}`}>
                        <FormField>
                            <Input
                                placeholder="%"
                                value={percentMonthlyAdjustment}
                                onChange={handlePercentMonthlyAdjustmentChange}
                                type="number"
                                disabled={fbUpdates.parsedFormDrivers.yearlyAverage[0]?.monthlyAdjustmentType !== MonthlyAdjustmentType.Percent}
                                onKeyDown={handleMonthlyAdjustmentInputKeyDown}
                            />
                        </FormField>
                    </div>
                </div>
                <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.radioRow}`}>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.radioCol}`}>
                        <FormField>
                            <Radio
                                name={MonthlyAdjustmentType.Dollar}
                                value={MonthlyAdjustmentType.Dollar}
                                checked={fbUpdates.parsedFormDrivers.yearlyAverage[0]?.monthlyAdjustmentType === MonthlyAdjustmentType.Dollar}
                                onChange={() => fbUpdates.updateYearlyAdjustmentType(MonthlyAdjustmentType.Dollar)}
                            />
                        </FormField>
                    </div>
                    <div className={`${monthlyAvgCss.col} ${monthlyAvgCss.inputCol} ${dollarMonthlyAdjustment !== undefined && monthlyAvgCss.dollarInput}`}>
                        <FormField>
                            <Input
                                placeholder="$"
                                value={dollarMonthlyAdjustment}
                                onChange={handleDollarMonthlyAdjustmentChange}
                                type="number"
                                disabled={fbUpdates.parsedFormDrivers.yearlyAverage[0]?.monthlyAdjustmentType !== MonthlyAdjustmentType.Dollar}
                                onKeyDown={handleMonthlyAdjustmentInputKeyDown}
                            />
                        </FormField>
                    </div>
                </div>
            </div>
        </div>
    );

    if (level === 0) {
        if (triggerNode) {
            return (
                <>
                    <div
                        className={css.triggerNode}
                        ref={triggerRef}
                        onClick={handleRootClick}
                    >
                        {triggerNode}
                    </div>
                    <TooltipModal
                        className={css.tooltipModal}
                        hasArrow={false}
                        referenceElement={refElement}
                        onClose={handleCloseRoot}
                        placement="bottom"
                        isAnimated
                    >
                        <TooltipModal.Body className={css.tooltipModalBody}>
                            {rootDropdown}
                        </TooltipModal.Body>
                    </TooltipModal>
                </>
            );
        }
        return rootDropdown;
    }

    if (level === 1) {
        if (triggerNode) {
            return (
                <>
                    <div
                        className={css.triggerNode}
                        ref={triggerRef}
                        onClick={handleRootClick}
                    >
                        {triggerNode}
                    </div>
                    <TooltipModal
                        className={css.tooltipModal}
                        hasArrow={false}
                        referenceElement={refElement}
                        onClose={handleCloseMonthlyAdjustmentNode}
                        placement="bottom"
                        isAnimated
                    >
                        <TooltipModal.Body className={css.tooltipModalBody}>
                            {monthlyAdjustmentForm}
                        </TooltipModal.Body>
                    </TooltipModal>
                </>
            );
        }
        return monthlyAdjustmentForm;
    }

    return <></>;
}

/**
 * To override ZDG styles and avoid using !important all the time
 */
const inlineStyles = ({
    rootMenu: {marginTop: "30px", position: "absolute" as 'absolute', transform: "none", width: "235px"},
    select: {backgroundColor: "transparent", border: "none", minWidth: "65px"},
    triggerNode: {cursor: "pointer"},
});
